// src/components/learning/prof/EcritureCanvas.js
// Zone d'écriture partagée entre prof et élève
// Réutilise la logique canvas de DocumentViewer.js
// Le prof voit ce que l'élève écrit en temps réel via Agora

import React, { useState, useEffect, useRef, useCallback } from 'react';

const C = {
  green: '#065f46',
  greenLight: '#E1F5EE',
  greenMid: '#1D9E75',
  red: '#E24B4A',
  blue: '#2563eb',
  muted: '#6b7280',
  text: '#1a1a1a',
};

// ─── AFFICHAGE LETTRE SIMPLE (tracé animé prévu en v2) ──────
const LetterTracingAnimation = ({ letter }) => {
  return (
    <div style={{ textAlign: 'center', marginBottom: 16 }}>
      <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
        Observe bien la lettre — tu vas la recopier ci-dessous
      </p>
      <div style={{
        background: '#fffef9',
        border: '1.5px solid #a7f3d0',
        borderRadius: 16,
        padding: '20px 16px',
        display: 'inline-block',
        marginBottom: 10,
        minWidth: 200,
        minHeight: 160,
        position: 'relative',
      }}>
        {/* Lignes de cahier */}
        {[60, 100, 140].map(y => (
          <div key={y} style={{
            position: 'absolute', left: 16, right: 16, top: y,
            height: 1, background: y === 100 ? '#d1fae5' : '#f0f0f0',
          }} />
        ))}
        {/* Lettre */}
        <div style={{
          fontSize: 110, lineHeight: 1,
          color: '#065f46',
          fontFamily: "'Amiri', 'Traditional Arabic', serif",
          direction: 'rtl',
          filter: 'drop-shadow(0 4px 12px rgba(6,95,70,0.2))',
          userSelect: 'none',
          position: 'relative', zIndex: 1,
        }}>
          {letter}
        </div>
        {/* Sens d'écriture */}
        <div style={{
          position: 'absolute', top: 8, right: 8,
          fontSize: 10, color: '#1D9E75', fontWeight: 600,
        }}>
          ← sens d'écriture
        </div>
      </div>
      <div style={{
        fontSize: 11, color: '#6b7280',
        background: '#f9f9f9', borderRadius: 8,
        padding: '6px 12px', display: 'inline-block',
      }}>
        ✏️ Animation du tracé — bientôt disponible
      </div>
    </div>
  );
};

// ─── CANVAS D'ÉCRITURE PARTAGÉ ─────────────────────────────
const EcritureCanvas = ({ client, userType, letter, lignes }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawings, setDrawings] = useState([]);
  const [color, setColor] = useState(userType === 'prof' ? '#ff0000' : '#2563eb');
  const [currentDrawingId, setCurrentDrawingId] = useState(null);
  const currentPathRef = useRef([]);

  const CANVAS_W = 460;
  const CANVAS_H = 280;

  // Écouter les annotations de l'autre côté
  useEffect(() => {
    const handleMsg = (event) => {
      const msg = event.detail;
      if (msg.type === 'ecriture_annotation') {
        const ann = msg.annotation;
        setDrawings(prev => {
          const existing = prev.find(d => d.id === ann.id);
          if (existing) {
            return prev.map(d => d.id === ann.id
              ? { ...d, points: [...d.points, ...ann.points] }
              : d
            );
          }
          return [...prev, ann];
        });
      } else if (msg.type === 'ecriture_clear') {
        setDrawings([]);
      }
    };
    window.addEventListener('agora-document-message', handleMsg);
    return () => window.removeEventListener('agora-document-message', handleMsg);
  }, []);

  // Rendu canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    // Lignes de guidage
    ctx.strokeStyle = '#f0f0f0';
    ctx.lineWidth = 1;
    for (let y = 60; y < CANVAS_H; y += 60) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(CANVAS_W, y);
      ctx.stroke();
    }

    // Dessiner tous les traits
    drawings.forEach(path => {
      if (!path.points || path.points.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = path.color || '#2563eb';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.moveTo(path.points[0].x, path.points[0].y);
      path.points.forEach(pt => ctx.lineTo(pt.x, pt.y));
      ctx.stroke();
    });
  }, [drawings]);

  const broadcastAnnotation = async (annotation) => {
    if (!client) return;
    try {
      const msg = { type: 'ecriture_annotation', annotation };
      const encoder = new TextEncoder();
      const data = JSON.stringify(msg);
      if (data.length < 900) {
        await client.sendStreamMessage(encoder.encode(data));
      }
    } catch (e) {
      console.error('Erreur broadcast annotation:', e);
    }
  };

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return {
      x: Math.round(e.clientX - rect.left),
      y: Math.round(e.clientY - rect.top),
    };
  };

  const startDrawing = (e) => {
    const pos = getPos(e);
    const id = Date.now() + Math.random();
    setCurrentDrawingId(id);
    currentPathRef.current = [pos];
    setIsDrawing(true);
    const newPath = { id, color, points: [pos] };
    setDrawings(prev => [...prev, newPath]);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const pos = getPos(e);
    currentPathRef.current.push(pos);

    setDrawings(prev => prev.map(d =>
      d.id === currentDrawingId
        ? { ...d, points: [...d.points, pos] }
        : d
    ));

    // Broadcast chunk de 10 points
    if (currentPathRef.current.length % 10 === 0) {
      broadcastAnnotation({
        id: currentDrawingId,
        color,
        points: currentPathRef.current.slice(-10),
      });
    }
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    setIsDrawing(false);
    // Broadcast points restants
    const remaining = currentPathRef.current.slice(
      -((currentPathRef.current.length % 10) || 10)
    );
    if (remaining.length > 0) {
      broadcastAnnotation({
        id: currentDrawingId,
        color,
        points: remaining,
      });
    }
    currentPathRef.current = [];
    setCurrentDrawingId(null);
  };

  const clearCanvas = async () => {
    setDrawings([]);
    if (client) {
      try {
        const msg = { type: 'ecriture_clear' };
        await client.sendStreamMessage(new TextEncoder().encode(JSON.stringify(msg)));
      } catch (e) {}
    }
  };

  return (
    <div>
      {/* Modèle à recopier — horizontal, tailles enfants */}
      <div style={{
        background: C.greenLight, border: `1px solid ${C.greenBorder || '#a7f3d0'}`,
        borderRadius: 12, padding: '16px 20px', marginBottom: 12,
      }}>
        <p style={{ fontSize: 12, color: C.muted, marginBottom: 12 }}>
          Modèle — lis et recopie dans la zone ci-dessous
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'center', direction: 'rtl' }}>
          {(lignes || []).map((l, i) => (
            <div key={i} style={{ textAlign: 'center' }}>
              <div style={{
                fontSize: l.type === 'phrase' ? 22 : l.type === 'mot' ? 36 : 52,
                fontFamily: "'Amiri', serif",
                color: C.green,
                lineHeight: 1.4,
                direction: 'rtl',
              }}>
                {l.ligne}
              </div>
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4, direction: 'ltr' }}>
                {l.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Barre outils */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginBottom: 8, padding: '8px 12px',
        background: '#f9f9f9', borderRadius: 10,
        border: '0.5px solid #e5e7eb',
      }}>
        <span style={{ fontSize: 12, color: C.muted }}>Couleur :</span>
        {['#2563eb', '#065f46', '#E24B4A', '#f59e0b', '#000'].map(col => (
          <div key={col} onClick={() => setColor(col)} style={{
            width: 22, height: 22, borderRadius: '50%',
            background: col,
            border: color === col ? '3px solid #333' : '2px solid #fff',
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            transition: 'transform 0.15s',
            transform: color === col ? 'scale(1.2)' : 'scale(1)',
          }} />
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={clearCanvas} style={{
          padding: '5px 12px', background: '#fff',
          border: '0.5px solid #e5e7eb', borderRadius: 20,
          fontSize: 11, color: C.muted, cursor: 'pointer',
        }}>
          🗑️ Effacer
        </button>
        {userType !== 'prof' && (
          <span style={{
            fontSize: 11, color: C.green,
            background: C.greenLight, padding: '3px 10px',
            borderRadius: 20, fontWeight: 600,
          }}>
            ✏️ Ton écriture visible par le prof
          </span>
        )}
        {userType === 'prof' && (
          <span style={{
            fontSize: 11, color: '#92400e',
            background: '#fffbeb', padding: '3px 10px',
            borderRadius: 20, fontWeight: 600,
          }}>
            👁️ Tu vois l'écriture de l'élève
          </span>
        )}
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={CANVAS_W}
        height={CANVAS_H}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        style={{
          border: '1.5px solid #e5e7eb',
          borderRadius: 12,
          cursor: 'crosshair',
          background: '#fff',
          display: 'block',
          width: '100%',
          maxWidth: CANVAS_W,
          touchAction: 'none',
        }}
      />
    </div>
  );
};

export { LetterTracingAnimation, EcritureCanvas };
export default EcritureCanvas;
